import React from "react";
import { Sparkles, Feather } from "lucide-react";

export const Philosophy = () => {
  return (
    <section
      id="craft"
      data-testid="philosophy-section"
      className="relative py-24 md:py-40"
    >
      <div className="mx-auto max-w-7xl px-6 md:px-10">
        <div className="mx-auto max-w-3xl text-center">
          <p className="eyebrow">The Craft</p>
          <h2
            data-testid="philosophy-heading"
            className="font-serif-display mt-4 text-4xl leading-tight text-[#F2EFEB] sm:text-5xl lg:text-6xl"
          >
            Two words that carry the studio.
          </h2>
          <p className="mx-auto mt-6 max-w-2xl text-base leading-relaxed text-[#A39D95]">
            Every game we make is built on the same two ideas. Learn them
            once — they run beneath both God Complex and Fatewoven.
          </p>
        </div>

        <div className="mt-20 grid grid-cols-1 gap-8 lg:grid-cols-2">
          {/* Why Tapestry */}
          <article
            data-testid="philosophy-tapestry"
            className="relative overflow-hidden gold-border p-10 md:p-14"
          >
            <span className="corner-orn tl" />
            <span className="corner-orn br" />
            <div className="relative z-10">
              <div className="flex items-center gap-3">
                <Feather className="h-4 w-4 text-[#C9A87C]" />
                <p className="eyebrow !text-[#C9A87C]">Why Tapestry</p>
              </div>
              <h3 className="font-serif-display mt-6 text-3xl leading-tight text-[#F2EFEB] md:text-4xl">
                The <em className="italic text-[#C9A87C]">threads</em>
                &nbsp;between everything.
              </h3>
              <div className="mt-8 space-y-5 text-[15px] leading-relaxed text-[#A39D95]">
                <p>
                  A Tapestry is the invisible weave of narrative threads
                  running between people, places, objects, and intentions —
                  the loyalties owed, the debts unpaid, the small promises
                  that shape how a story turns.
                </p>
                <p>
                  Every Mythos is a Tapestry made visible, pulled taut by
                  the choices at the table. In Fatewoven, characters find
                  themselves drawn into many at once — figures of fate whose
                  lives cross warp and weft with kings, guilds, gods, and
                  the strangers who watch from the edges.
                </p>
                <p className="text-[#C9A87C]">
                  Every table leaves the tapestry a little different than
                  they found it.
                </p>
              </div>
            </div>
          </article>

          {/* What is a Mythos */}
          <article
            data-testid="philosophy-mythos"
            className="relative overflow-hidden gold-border p-10 md:p-14"
          >
            <span className="corner-orn tr" />
            <span className="corner-orn bl" />
            <div className="relative z-10">
              <div className="flex items-center gap-3">
                <Sparkles className="h-4 w-4 text-[#C9A87C]" />
                <p className="eyebrow !text-[#C9A87C]">What is a Mythos</p>
              </div>
              <h3 className="font-serif-display mt-6 text-3xl leading-tight text-[#F2EFEB] md:text-4xl">
                Not a campaign. A{" "}
                <em className="italic text-[#C9A87C]">place</em>.
              </h3>
              <div className="mt-8 space-y-5 text-[15px] leading-relaxed text-[#A39D95]">
                <p>
                  Elsewhere in the hobby they call it a Campaign — a term
                  inherited from war games, when play meant moving armies
                  across a map toward a distant objective. That word
                  describes a journey.
                </p>
                <p>
                  A <span className="text-[#F2EFEB]">Mythos</span> describes
                  a place. It begins with a hub — a city, a court, an
                  island — full of factions with incompatible desires. It
                  ends when the players have become forces within that hub,
                  not merely visitors passing through it. Many goals
                  instead of a single quest. A legacy instead of a
                  finale.
                </p>
                <p>
                  In God Complex you gather as a{" "}
                  <span className="text-[#F2EFEB]">Pantheon</span>. In
                  Fatewoven, a{" "}
                  <span className="text-[#F2EFEB]">Company</span>. In
                  either, the tapestry you leave behind is yours to have
                  woven.
                </p>
              </div>
            </div>
          </article>
        </div>
      </div>
    </section>
  );
};

export default Philosophy;
